<template>
    <header class="mb-4">
        <div
            class="relative"
            v-if="
                activeRedemption?.hotel &&
                activeRedemption?.redemption_type?.slug === 'hotel'
            "
        >
            <ImageLoader>
                <template #image>
                    <img
                        :src="activeRedemption?.hotel?.image_location"
                        class="w-full h-52 object-cover rounded-lg mb-4"
                    />
                </template>
                <template #preloader>
                    <div
                        class="w-full h-52 object-cover rounded-lg mb-4 bg-slate-200 dark:bg-slate-700 shimmer"
                    ></div>
                </template>
                <template #error>
                    <div
                        class="w-full h-52 object-cover rounded-lg mb-4 flex items-center justify-center text-center flex-col bg-slate-100 dark:bg-slate-700 border border-slate-200 dark:border-slate-700"
                    >
                        <Icon icon="no-image" />
                        <p class="text-sm text-slate-600 dark:text-slate-300">No Image</p>
                    </div>
                </template>
            </ImageLoader>
            <div
                class="absolute bottom-2 right-2 bg-white/90 dark:bg-slate-800/90 p-1 rounded justify-center items-center flex"
                v-if="activeRedemption?.hotel?.program?.program_image"
            >
                <img
                    :src="`${cdnUrl}/${activeRedemption?.hotel?.program?.program_image?.path}/_small/${activeRedemption?.hotel?.program?.program_image?.file_name}`"
                    class="max-w-[68px] h-auto max-h-7"
                />
            </div>
        </div>
        <div
            class="flex gap-4 mb-4 items-center sm:items-start"
            :class="
                activeRedemption?.hotel &&
                activeRedemption?.redemption_type?.slug === 'hotel'
                    ? 'items-start'
                    : 'items-center'
            "
        >
            <div
                class="w-12 min-w-12 min-h-12 h-12 flex bg-[#fff1e4] text-[#ff7d01] rounded-full justify-center items-center"
            >
                <TripsIcon
                    class="text-[#ff7d01]"
                    :icon="activeRedemption?.redemption_type?.icon"
                />
            </div>
            <div>
                <h3
                    :class="
                        activeRedemption?.status === 'cancelled'
                            ? 'text-slate-400 text-xl font-medium line-through'
                            : 'text-slate-800 dark:text-slate-100 text-xl font-medium'
                    "
                >
                    {{ panelTitle }}
                </h3>
                <address
                    class="text-slate-500 dark:text-slate-400 text-sm not-italic hidden sm:block"
                    v-if="
                        activeRedemption?.hotel &&
                        activeRedemption?.redemption_type?.slug === 'hotel'
                    "
                >
                    {{ activeRedemption?.hotel?.address_1 }},
                    {{ activeRedemption?.hotel?.locality }}
                    {{ activeRedemption?.hotel?.region }}
                    {{ activeRedemption?.hotel?.postal_code }},
                    {{ activeRedemption?.hotel?.country }}
                </address>
                <UiTooltip
                    v-if="activeRedemption?.source === 'email_import'"
                    text="Redemption imported from email"
                    placement="top"
                >
                    <template #trigger>
                        <div class="flex items-center gap-2 rounded mt-1">
                            <Icon
                                icon="email-forward"
                                :width="20"
                                :height="20"
                                class="flex w-5 h-5 justify-center items-center text-[#64748B] dark:text-slate-400"
                                pathClass="fill-[#64748B]"
                            />
                            <span
                                class="text-sm font-normal leading-5 text-[#64748B] dark:text-slate-400"
                            >
                                From email
                            </span>
                        </div>
                    </template>
                </UiTooltip>

                <!-- Status Badges -->
                <div class="flex items-center gap-2 mt-1">
                    <UiTooltip
                        v-if="activeRedemption?.status === 'cancelled'"
                        placement="top"
                    >
                        <template #trigger>
                            <div
                                class="flex items-center gap-1 rounded bg-red-100 px-2 py-1"
                            >
                                <Icon
                                    icon="x-circle"
                                    :width="16"
                                    :height="16"
                                    class="text-red-600"
                                    pathClass="fill-red-600"
                                />
                                <span class="text-xs font-medium text-red-800">
                                    Cancelled
                                </span>
                            </div>
                        </template>
                        Cancelled{{
                            activeRedemption?.cancelled_at
                                ? ' on ' +
                                  new Date(
                                      activeRedemption.cancelled_at
                                  ).toLocaleDateString()
                                : ''
                        }}
                    </UiTooltip>

                    <UiTooltip
                        v-if="activeRedemption?.is_modified"
                        text="This redemption has been modified"
                        placement="top"
                    >
                        <template #trigger>
                            <div
                                class="flex items-center gap-1 rounded bg-blue-100 px-2 py-1"
                            >
                                <Icon
                                    icon="edit"
                                    :width="16"
                                    :height="16"
                                    class="text-blue-600"
                                    pathClass="fill-blue-600"
                                />
                                <span class="text-xs font-medium text-blue-800">
                                    Modified
                                </span>
                            </div>
                        </template>
                    </UiTooltip>
                </div>
            </div>
        </div>
        <div
            class="flex flex-wrap items-center gap-2 sm:gap-4"
            v-if="!isSharedView"
        >
            <button
                class="btn btn-secondary flex items-center gap-2"
                @click="editRedemption()"
            >
                <Icon icon="pencil" />
                <span>Edit</span>
            </button>
            <button
                v-if="activeRedemption?.source === 'email_import'"
                class="btn btn-secondary flex items-center gap-2"
                @click="$emit('move-to-trip')"
            >
                <Icon icon="direct-connection" />
                <span>Move to trip</span>
            </button>
            <button
                class="btn btn-secondary flex items-center gap-2"
                @click="tripsStore.setShowDeleteRedemptionConfirm(true)"
            >
                <Icon icon="trash" />
                <span>Delete</span>
            </button>
        </div>
    </header>
</template>
<script setup>
import { computed } from 'vue';
import { useTripsStore } from '@/store/trips';
import TripsIcon from '@/components/trips/icons.vue';
import Icon from '@/components/ui/icon/icon.vue';
import ImageLoader from '@/components/ui/image-loader/image-loader.vue';
import UiTooltip from '@/components/ui/tooltip/ui-tooltip.vue';
import { useTripTitles } from '@/composables/tripTitles';

const emit = defineEmits(['move-to-trip']);

const tripsStore = useTripsStore();

const props = defineProps({
    activeRedemption: Object,
    isSharedView: {
        type: Boolean,
        default: false,
    },
});

const cdnUrl = computed(() => window.cdnUrl);

const redemptionRef = computed(() => props.activeRedemption);
const { flightTitle, trainTitle, rentalTitle } = useTripTitles(redemptionRef);

const editRedemption = () => {
    tripsStore.setActiveRedemption(props.activeRedemption);
    tripsStore.setShowRedemptionEditModal(true);
};

const panelTitle = computed(() => {
    if (props.activeRedemption?.redemption_type?.slug === 'flight') {
        return flightTitle.value;
    }

    if (props.activeRedemption?.redemption_type?.slug === 'train') {
        return trainTitle.value;
    }

    if (props.activeRedemption?.redemption_type?.slug === 'rental-car') {
        return rentalTitle.value;
    }

    return (
        props.activeRedemption?.name ||
        props.activeRedemption?.redemption_type?.name ||
        ''
    );
});
</script>
